// Operations view of a booking as the approved state machine (0227), and the
// Superadmin-only Replace Therapist action (0230, plan S4/S6).
//
// States are READ from `v_ops_booking_entries`, never re-derived — see
// packages/logic/booking-lifecycle.ts for why. The view is hub-scoped in SQL
// (hub_manager sees only their JWT hub) and carries no client identity.

import {
  parseBookingEntryState,
  parseBookingLifecycleState,
  type BookingEntryState,
  type BookingLifecycleState,
} from "@bednbody/logic";
import type { ReplaceTherapistEntryInput } from "@bednbody/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { callEdgeFunction } from "@/lib/edge-functions";
import { supabase } from "@/lib/supabase";

export interface OpsBookingEntry {
  therapistId: string | null;
  leadBookingId: string;
  bookingIds: string[];
  ref: string | null;
  hubId: string | null;
  clientCount: number;
  bookingKind: string;
  windowStartsAt: string | null;
  windowEndsAt: string | null;
  leadStatus: string | null;
  state: BookingEntryState | null;
  bookingState: BookingLifecycleState | null;
}

/** Ops copy for each entry state — the spec's own terms (`Canonical Terminology`). */
export const OPS_ENTRY_STATE_LABEL: Record<BookingEntryState, string> = {
  pending: "Pending",
  scheduled: "Scheduled",
  finding_rider: "Finding Rider",
  searching_rider: "Searching Rider",
  reassigning: "Reassigning",
  active: "Active",
  completed: "Completed",
  client_cancelled: "Client Cancelled",
  system_cancelled: "System Cancelled",
};

const entriesKey = (bookingGroupId: string) => ["ops-booking-entries", bookingGroupId] as const;

/** Every Therapist entry of one booking group. Polls: two states change by the clock alone. */
export function useOpsBookingEntries(bookingGroupId: string | null) {
  return useQuery({
    queryKey: entriesKey(bookingGroupId ?? ""),
    enabled: bookingGroupId !== null,
    refetchInterval: 30_000,
    queryFn: async (): Promise<OpsBookingEntry[]> => {
      const { data, error } = await supabase
        .from("v_ops_booking_entries")
        .select(
          "therapist_id, lead_booking_id, booking_ids, ref, hub_id, client_count, booking_kind, window_starts_at, window_ends_at, lead_status, entry_state, booking_state",
        )
        .eq("booking_group_id", bookingGroupId!)
        .order("window_starts_at");
      if (error) throw error;
      return (data ?? []).flatMap((row) =>
        row.lead_booking_id
          ? [
              {
                therapistId: row.therapist_id,
                leadBookingId: row.lead_booking_id,
                bookingIds: row.booking_ids ?? [],
                ref: row.ref,
                hubId: row.hub_id,
                clientCount: row.client_count ?? 0,
                bookingKind: row.booking_kind ?? "unknown",
                windowStartsAt: row.window_starts_at,
                windowEndsAt: row.window_ends_at,
                leadStatus: row.lead_status,
                state: parseBookingEntryState(row.entry_state),
                bookingState: parseBookingLifecycleState(row.booking_state),
              },
            ]
          : [],
      );
    },
  });
}

/**
 * THER-040 for a whole Therapist entry. ⚠ The route is Superadmin-only: who may
 * replace a locked Therapist is an Open Item, so a screen must not offer this
 * to any other role — the server refuses it either way.
 */
export function useReplaceTherapistEntry(bookingGroupId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { fromTherapistId: string } & ReplaceTherapistEntryInput) =>
      callEdgeFunction<{ outcome: "replaced"; replaced: number; booking_ids: string[] }>(
        `admin/bookings/${bookingGroupId}/therapists/${vars.fromTherapistId}/replace`,
        { data: { toTherapistId: vars.toTherapistId, reason: vars.reason } },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: entriesKey(bookingGroupId) }),
  });
}

/** Staff names for the entry cards — internal staff data, never client PII. */
export function useStaffNames(ids: readonly string[]) {
  const key = [...ids].sort();
  return useQuery({
    queryKey: ["staff-names", key],
    enabled: key.length > 0,
    queryFn: async (): Promise<Map<string, string>> => {
      const { data, error } = await supabase.from("staff").select("id, full_name").in("id", key);
      if (error) throw error;
      return new Map((data ?? []).map((row) => [row.id, row.full_name ?? "Unnamed"]));
    },
  });
}

/**
 * Who could take the seat: active therapists of the booking's OWN hub (0230
 * refuses any other — the hub→client distance was checked for this hub). Full
 * eligibility (duty, qualification, commitments) is decided by the server; a
 * name listed here can still be refused, and the dialog shows why.
 */
export function useHubTherapists(hubId: string | null, excludeId: string | null) {
  return useQuery({
    queryKey: ["hub-therapists", hubId, excludeId],
    enabled: hubId !== null,
    queryFn: async (): Promise<{ id: string; fullName: string }[]> => {
      const { data, error } = await supabase
        .from("staff")
        .select("id, full_name")
        .eq("hub_id", hubId!)
        .eq("role", "therapist")
        .eq("active", true)
        .order("full_name");
      if (error) throw error;
      return (data ?? [])
        .filter((row) => row.id !== excludeId)
        .map((row) => ({ id: row.id, fullName: row.full_name ?? "Unnamed" }));
    },
  });
}
